import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { ShoppingBag, User, Menu, X, LogOut } from "lucide-react";
import { useState, useEffect } from "react";

export default function Navbar({ variant = "dark" }) {
    const { user, logout } = useAuth();
    const { count } = useCart();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const isAdminArea = location.pathname.startsWith("/admin");

    const baseText = "text-wine-dark";
    const headerBg = scrolled
        ? "bg-white shadow-sm border-b border-gold-soft/40"
        : "bg-white border-b border-gold-soft/30";

    const linkBase =
        "label-overline relative transition-colors duration-300 hover:text-gold-soft";

    const dashHref = user
        ? user.role === "admin"
            ? "/admin"
            : user.role === "vendor"
            ? "/vendor"
            : "/conta"
        : "/login";

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerBg}`}
            data-testid="navbar"
        >
            <div className="container-edge h-24 flex items-center justify-between">
                {isAdminArea ? (
                    <div className={`${baseText} flex items-center gap-3`} data-testid="logo">
                        <img
                            src="https://customer-assets.emergentagent.com/job_curated-pt/artifacts/nm3euu0k_logo1_ocaseirinho.png"
                            alt="O Caseirinho"
                            className="h-14 md:h-16 w-auto object-contain"
                        />
                    </div>
                ) : (
                    <Link to="/" className={`${baseText} flex items-center gap-3`} data-testid="logo-link">
                        <img
                            src="https://customer-assets.emergentagent.com/job_curated-pt/artifacts/nm3euu0k_logo1_ocaseirinho.png"
                            alt="O Caseirinho"
                            className="h-14 md:h-16 w-auto object-contain"
                        />
                    </Link>
                )}

                {!isAdminArea && (
                    <nav className={`hidden lg:flex items-center gap-10 ${baseText}`}>
                        <NavLink to="/marketplace" className={linkBase} data-testid="nav-marketplace">
                            Marketplace
                        </NavLink>
                        <NavLink to="/produtores" className={linkBase} data-testid="nav-producers">
                            Produtores
                        </NavLink>
                        <NavLink to="/clube" className={linkBase} data-testid="nav-clube">
                            Clube
                        </NavLink>
                        <NavLink to="/blog" className={linkBase} data-testid="nav-blog">
                            Diário
                        </NavLink>
                    </nav>
                )}
                {isAdminArea && (
                    <span className="hidden lg:inline-flex label-overline text-wine/60 tracking-[0.3em]">
                        Área de Administração
                    </span>
                )}

                <div className={`flex items-center gap-5 ${baseText}`}>
                    {user ? (
                        <>
                            <Link
                                to={dashHref}
                                className="hidden md:inline-flex items-center gap-2 text-xs label-overline hover:text-gold-soft transition-colors"
                                data-testid="nav-account"
                            >
                                <User className="h-4 w-4" strokeWidth={1.5} />
                                {user.name?.split(" ")[0]}
                            </Link>
                            <button
                                onClick={async () => {
                                    await logout();
                                    navigate("/");
                                }}
                                className="hidden md:inline-flex"
                                data-testid="logout-btn"
                                title="Sair"
                            >
                                <LogOut className="h-4 w-4" strokeWidth={1.5} />
                            </button>
                        </>
                    ) : (
                        <Link
                            to="/login"
                            className="hidden md:inline-flex items-center gap-2 text-xs label-overline hover:text-gold-soft transition-colors"
                            data-testid="nav-login"
                        >
                            <User className="h-4 w-4" strokeWidth={1.5} />
                            Entrar
                        </Link>
                    )}
                    {!isAdminArea && (
                        <Link
                            to="/carrinho"
                            className="relative inline-flex items-center"
                            data-testid="nav-cart"
                        >
                            <ShoppingBag className="h-5 w-5" strokeWidth={1.5} />
                        {count > 0 && (
                            <span className="absolute -top-2 -right-3 bg-gold-soft text-wine-dark text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                {count}
                            </span>
                        )}
                        </Link>
                    )}
                    <button
                        className="lg:hidden"
                        onClick={() => setOpen(!open)}
                        data-testid="mobile-menu-toggle"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    className="lg:hidden bg-white text-wine-dark border-t border-gold-soft/40"
                    data-testid="mobile-menu"
                >
                    <div className="container-edge py-8 flex flex-col gap-6">
                        {(isAdminArea ? [
                            [dashHref, "Dashboard Admin"],
                        ] : [
                            ["/marketplace", "Marketplace"],
                            ["/produtores", "Produtores"],
                            ["/clube", "Clube"],
                            ["/blog", "Diário"],
                            [dashHref, user ? "A minha conta" : "Entrar"],
                        ]).map(([href, label]) => (
                            <Link
                                key={href}
                                to={href}
                                onClick={() => setOpen(false)}
                                className="label-overline hover:text-gold-soft"
                            >
                                {label}
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
}
